/**
 * Metro Bundler Error Detection & Enrichment
 *
 * Detects runtime errors caused by Metro bundler issues and adds proper
 * Sentry tags for automated routing to Linear.
 *
 * Integration Points:
 * - Called from global error handlers (web & native)
 * - Enriches Sentry events with Metro-specific context
 * - Enables Linear routing via component:metro tag
 */

export interface MetroErrorSignature {
  isMetroError: boolean;
  errorType?: 'module_resolution' | 'sdk_version_mismatch' | 'circular_dependency' | 'transform_error';
  component?: string;
  priority?: 'critical' | 'high' | 'medium' | 'low';
  suggestedFix?: string;
  relatedFiles?: string[];
}

// ============================================================================
// Error Pattern Detection
// ============================================================================

/**
 * Detects if an error is Metro bundler-related based on error message patterns
 */
export function detectMetroError(error: Error): MetroErrorSignature {
  const message = error.message || '';
  const stack = error.stack || '';
  const combinedText = `${message}\n${stack}`;

  // Pattern 1: Module resolution errors
  if (
    message.includes('Cannot find module') ||
    message.includes('Unable to resolve') ||
    message.includes('Module not found')
  ) {
    const moduleMatch = message.match(/['"]([^'"]+)['"]/);
    const moduleName = moduleMatch ? moduleMatch[1] : 'unknown';

    return {
      isMetroError: true,
      errorType: 'module_resolution',
      component: `metro-bundler`,
      priority: 'high',
      suggestedFix: `Check if module '${moduleName}' is installed and Metro config includes correct sourceExts`,
      relatedFiles: [moduleName],
    };
  }

  // Pattern 2: SDK version mismatch (like our startTransaction case)
  if (
    message.includes('is not a function') ||
    message.includes('is not a constructor') ||
    message.includes('is undefined')
  ) {
    const methodMatch = message.match(/(\w+) is not a function/);
    const methodName = methodMatch ? methodMatch[1] : 'unknown';

    // Check if it's a known SDK method
    const knownSdkMethods = ['startTransaction', 'startSpan', 'captureException', 'setContext'];
    if (knownSdkMethods.includes(methodName)) {
      return {
        isMetroError: true,
        errorType: 'sdk_version_mismatch',
        component: 'metro-bundler',
        priority: 'critical',
        suggestedFix: `SDK version mismatch detected. Check if ${methodName} is available in the current SDK version. May need platform-specific implementation (.web.ts / .native.ts)`,
        relatedFiles: extractFilesFromStack(stack),
      };
    }

    // Generic "not a function" error from bundled code
    if (stack.includes('webpack') || stack.includes('metro')) {
      return {
        isMetroError: true,
        errorType: 'transform_error',
        component: 'metro-bundler',
        priority: 'high',
        suggestedFix: `Bundler transform error. Clear Metro cache: pnpm cache:clear`,
        relatedFiles: extractFilesFromStack(stack),
      };
    }
  }

  // Pattern 3: Circular dependency
  if (
    message.includes('Circular dependency') ||
    message.includes('Maximum call stack size exceeded') ||
    stack.includes('at Module._compile')
  ) {
    return {
      isMetroError: true,
      errorType: 'circular_dependency',
      component: 'metro-bundler',
      priority: 'high',
      suggestedFix: 'Use @rnx-kit/metro-plugin-cyclic-dependencies-detector to identify circular imports',
      relatedFiles: extractFilesFromStack(stack),
    };
  }

  // Pattern 4: Transform/Babel errors
  if (
    message.includes('SyntaxError') ||
    message.includes('Unexpected token') ||
    message.includes('Transform error')
  ) {
    return {
      isMetroError: true,
      errorType: 'transform_error',
      component: 'metro-bundler',
      priority: 'high',
      suggestedFix: 'Metro transform error. Check Babel config and clear cache',
      relatedFiles: extractFilesFromStack(stack),
    };
  }

  // Not a Metro error
  return {
    isMetroError: false,
  };
}

/**
 * Extract file paths from stack trace
 */
function extractFilesFromStack(stack: string): string[] {
  const filePattern = /at .+? \((.+?):\d+:\d+\)/g;
  const matches = stack.matchAll(filePattern);
  const files = new Set<string>();

  for (const match of matches) {
    // Filter out node_modules and system files
    const file = match[1];
    if (!file.includes('node_modules') && !file.startsWith('node:')) {
      files.add(file);
    }
  }

  return Array.from(files).slice(0, 5); // Limit to 5 most relevant files
}

// ============================================================================
// Platform Detection
// ============================================================================

/**
 * Detect which platform the error occurred on
 */
export function detectPlatform(): 'web' | 'ios' | 'android' | 'unknown' {
  if (typeof window !== 'undefined') {
    // Web platform
    return 'web';
  }

  if (typeof navigator !== 'undefined') {
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/android/i.test(userAgent)) {
      return 'android';
    }
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'ios';
    }
  }

  return 'unknown';
}

// ============================================================================
// Sentry Event Enrichment
// ============================================================================

/**
 * Enriches Sentry event with Metro error context
 * Call this from beforeSend hook or error boundaries
 */
export function enrichMetroError(
  event: any,
  hint?: { originalException?: Error }
): any {
  const error = hint?.originalException;
  if (!error || !(error instanceof Error)) {
    return event;
  }

  const signature = detectMetroError(error);
  if (!signature.isMetroError) {
    return event;
  }

  // Add Metro-specific tags for filtering and Linear routing
  event.tags = {
    ...event.tags,
    component: signature.component,
    error_category: 'metro-bundler',
    error_type: signature.errorType,
    priority: signature.priority,
    platform: detectPlatform(),
  };

  // Add context for debugging
  event.contexts = {
    ...event.contexts,
    metro_error: {
      type: signature.errorType,
      suggested_fix: signature.suggestedFix,
      related_files: signature.relatedFiles,
      is_metro_error: true,
    },
  };

  // Set fingerprint for proper grouping
  event.fingerprint = [
    'metro',
    signature.errorType || 'unknown',
    error.message.split(':')[0], // First part of error message
  ];

  // Add breadcrumb
  event.breadcrumbs = event.breadcrumbs || [];
  event.breadcrumbs.push({
    category: 'metro-error',
    message: 'Metro bundler error detected',
    level: 'error',
    data: {
      error_type: signature.errorType,
      suggested_fix: signature.suggestedFix,
    },
  });

  return event;
}

// ============================================================================
// Error Boundary Integration
// ============================================================================

/**
 * React Error Boundary helper
 * Use this in your error boundary's componentDidCatch
 */
export function reportMetroErrorToBoundary(
  error: Error,
  errorInfo: React.ErrorInfo
): void {
  const signature = detectMetroError(error);

  if (signature.isMetroError) {
    console.error('🔧 Metro Bundler Error Detected:', {
      type: signature.errorType,
      priority: signature.priority,
      fix: signature.suggestedFix,
      files: signature.relatedFiles,
    });

    // Add to error message for better visibility
    const enrichedError = new Error(
      `[Metro Error: ${signature.errorType}] ${error.message}\n\n` +
      `Suggested Fix: ${signature.suggestedFix}`
    );
    enrichedError.stack = error.stack;

    // Re-throw with enriched message
    throw enrichedError;
  }
}
